import { styled } from "goober";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import { zIndexes } from "../lib/zIndexes";
import { AnchoredPopover } from "./AnchoredPopover";

// Global state for tooltip "warm" period - when a tooltip was recently shown,
// subsequent tooltips appear instantly without delay
let lastTooltipCloseTime = 0;
export const TOOLTIP_DELAY = 300;
export const WARM_PERIOD = 500;
const MAX_WIDTH = 300;

export function getLastTooltipCloseTime() {
  return lastTooltipCloseTime;
}
export function setLastTooltipCloseTime(t = Date.now()) {
  lastTooltipCloseTime = t;
}

const Container = styled("div")`
  display: inline-flex;
`;

const Trigger = styled("div")`
  display: inline-flex;
  border-radius: 8px;
  cursor: pointer;
`;

const Label = styled("span")`
  font-weight: 450;
  font-size: 10px;
  line-height: 12px;
  letter-spacing: 0;
`;

const PopoverContent = styled(AnchoredPopover)`
  background: var(--tooltip-bg);
  color: var(--tooltip-fg);
  --text-secondary: var(--tooltip-fg-subtle);
  box-shadow: var(--elevation-low);
  padding: 6px 8px;
  z-index: ${zIndexes.tooltip};
  max-width: ${MAX_WIDTH}px;
  width: max-content;
  overflow-wrap: anywhere;
  pointer-events: none;
  text-align: left;
  font-size: 10px;
`;

export type Align = "center" | "left" | "right";
export type Vertical = "above" | "below";

export function getPositionStyles(
  align: Align,
  vertical: Vertical
): React.CSSProperties {
  const styles: Record<string, string> = {};

  if (vertical === "below") {
    styles.top = "anchor(bottom)";
    styles.marginTop = "6px";
  } else {
    styles.bottom = "anchor(top)";
    styles.marginBottom = "6px";
  }

  if (align === "left") {
    styles.left = "anchor(left)";
    styles.translate = "-8px 0";
  } else if (align === "right") {
    styles.right = "anchor(right)";
    styles.translate = "8px 0";
  } else {
    styles.left = "anchor(center)";
    styles.translate = "-50% 0";
  }

  return styles as React.CSSProperties;
}

type Props = {
  content: React.ReactNode;
  children: React.ReactNode;
  align?: Align;
  vertical?: Vertical;
  contentStyle?: React.CSSProperties;
  hidden?: boolean;
  shouldShow?: () => boolean;
  forceOpen?: boolean;
  selectable?: boolean;
  // Make the wrappers stretch and shrink with their parent (rather than
  // sizing to content) so a child can ellipsis-truncate to the available width.
  // Use "stretch" to also fill the available space for full-width controls.
  fill?: boolean | "stretch";
  // Open beside the trigger, vertically centered, instead of the
  // default above/below placement.
  side?: "top" | "left" | "right";
};

export function Tooltip({
  content,
  children,
  align: forcedAlign,
  vertical: forcedVertical,
  contentStyle,
  hidden,
  shouldShow,
  forceOpen,
  selectable,
  fill,
  side,
}: Props) {
  const [isVisible, setIsVisible] = useState(false);
  const [align, setAlign] = useState<Align>("center");
  const [automaticVertical, setAutomaticVertical] = useState<Vertical>("above");
  const triggerRef = useRef<HTMLDivElement>(null);
  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const computePosition = () => {
    if (side === "left" || side === "right") return;
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    // A centered tooltip needs up to half its max width of room on each side,
    // so anchors within that of an edge align to the edge instead of clipping.
    if (forcedAlign) {
      setAlign(forcedAlign);
    } else if (rect.left < MAX_WIDTH / 2) {
      setAlign("left");
    } else if (viewportWidth - rect.right < MAX_WIDTH / 2) {
      setAlign("right");
    } else {
      setAlign("center");
    }

    setAutomaticVertical(rect.top < 60 ? "below" : "above");
  };

  const showTooltip = () => {
    computePosition();
    setIsVisible(true);
  };

  const handlePointerEnter = (event: React.PointerEvent) => {
    if (hidden || event.pointerType === "touch" || shouldShow?.() === false)
      return;
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
    const isWarm = Date.now() - getLastTooltipCloseTime() < WARM_PERIOD;
    if (isWarm) {
      showTooltip();
    } else {
      delayTimerRef.current = setTimeout(showTooltip, TOOLTIP_DELAY);
    }
  };

  const hideTooltip = () => {
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
    if (!isVisible) return;
    setLastTooltipCloseTime(Date.now());
    setIsVisible(false);
  };

  const handlePointerLeave = () => {
    if (selectable && isVisible) {
      if (delayTimerRef.current) clearTimeout(delayTimerRef.current);
      delayTimerRef.current = setTimeout(hideTooltip, TOOLTIP_DELAY);
    } else hideTooltip();
  };

  useEffect(() => {
    return () => {
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (hidden) {
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }
      // oxlint-disable-next-line react/set-state-in-effect -- EffectSetState: hides the tooltip and clears its open timer when the hidden prop flips
      setIsVisible(false);
    }
  }, [hidden]);

  const updatePosition = useEffectEvent(computePosition);
  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect -- EffectSetState: recomputes the anchored DOM position when forced open
    if (forceOpen) updatePosition();
  }, [forceOpen, forcedAlign, side]);

  const fillStyle: React.CSSProperties | undefined = fill
    ? {
        display: "flex",
        flexGrow: fill === "stretch" ? 1 : 0,
        flexShrink: 1,
        flexBasis: "auto",
        minWidth: 0,
        maxWidth: "100%",
        cursor: "inherit",
      }
    : undefined;

  const positionStyles: React.CSSProperties =
    side === "left"
      ? { right: "anchor(left)", top: "anchor(center)", translate: "-6px -50%" }
      : side === "right"
        ? {
            left: "anchor(right)",
            top: "anchor(center)",
            translate: "6px -50%",
          }
        : getPositionStyles(align, forcedVertical ?? automaticVertical);

  return (
    <Container
      style={fillStyle}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      <PopoverContent
        isOpen={isVisible || !!forceOpen}
        style={{
          ...positionStyles,
          ...contentStyle,
          ...(selectable
            ? { pointerEvents: "auto", userSelect: "text" }
            : undefined),
        }}
        trigger={(props) => (
          <Trigger
            ref={triggerRef}
            {...props}
            onPointerDownCapture={hideTooltip}
            onClickCapture={hideTooltip}
            onKeyDownCapture={(event) => {
              if (["Enter", " ", "Escape"].includes(event.key)) hideTooltip();
            }}
            style={{
              ...fillStyle,
              ...(props as { style?: React.CSSProperties }).style,
            }}
          >
            {children}
          </Trigger>
        )}
      >
        {typeof content === "string" ? <Label>{content}</Label> : content}
      </PopoverContent>
    </Container>
  );
}
